import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { IAvailableForms, IGeneralRegistrationDTO, IRegistrationFieldsErrorConfig, IRegistrationSettings, RegistrationTypeValue } from '../../../../../../../api/registration-configuration.dao';
import {
    LONG_RICH_TEXT_LENGTH,
    SHORT_PLAIN_TEXT_64_LENGTH
} from '../../../../../../../constants/common.constants';
import {
    MAX_LENGTH_SHORT_PLAIN_TEXT_64_NOT_REQUIRED_ERROR_MESSAGE,
    REQUIRED_ERROR_MESSAGE,
    TEXT_TOO_LONG_ERROR_MESSAGE
} from '../../../../../../../constants/validation-errors';
import { calcByteCodeSize, isFieldExistInArray } from '../../../../../../../infrastructure/utils/common.utils';
import {DOMAIN_REG_EXP, HTML_TAGS_REG_EXP, NEW_LINE_REG_EXP} from '../../../../../../../constants/regex.constants';
import {
    convertBoolToDefaultOrCustomTypeForMixPanel,
    convertBoolTypeForMixPanel,
    isTrackingFieldEnabled
} from '../../../../../../../tracking-service/tracking-values';
import { hasPermission } from '../../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../../api/roles.dao';
import { transpileInlineStylesToClasses, transpileClassesToInlineStyles } from '../../../../../../global/rich-text-editor/utils/transpiler';

const defaultValues = {
    asLandingPage: false,
    triagedRegistrationEnabled: false,
    triagedQuestionsCreated: false,
    registrationApprovalEnabled: false,
    urlParameterRegistrationEnabled: false,
};

export const defaultSettingsValues = {
    autoSignInEnabled: false,
    calendarButtonEnabled: false,
    header: 'Register',
    instructions: '',
    registrationSubmittedMessage: 'Thank you for registering. Please check your email for details of how to attend the event.',
    pendingDeniedMessage: '',
    registrationType: RegistrationTypeValue.OPEN,
    showOpenMeetings: false
};

interface IGeneralRegistrationFormOptions {
    closeConfigureRegistrationForm: () => void;
    onApply: () => Promise<boolean>;
    getIsSaving: () => boolean;
}

export default class GeneralRegistrationVM {
    @mobx.observable
    public registrationAttendeeEnabled: boolean;
    @mobx.observable
    private asLandingPage: boolean;
    @mobx.observable
    private blockedDomains: string;
    @mobx.observable
    private blockedDomainsError: string | null;
    @mobx.observable
    public showFormRegistrationFormPopup: () => void;
    @mobx.observable
    private triagedRegistrationEnabled: boolean;
    @mobx.observable
    public generalRegistrationFormOptions: IGeneralRegistrationFormOptions;
    @mobx.observable
    private triagedQuestionsCreated: boolean;
    @mobx.observable
    private registrationFieldsErrorHandler: Record<string, IRegistrationFieldsErrorConfig> = {};

    @mobx.observable
    public settings: IRegistrationSettings[] = [];
    @mobx.observable
    public settingsFormNames: string[] = [];
    @mobx.observable
    public availableForms: IAvailableForms[] = [];
    @mobx.observable
    public passportRegistrationApprovalEnabled: boolean;
    @mobx.observable
    public registrationApprovalEnabled: boolean;
    @mobx.observable
    public urlParameterRegistrationEnabled: boolean;
    @mobx.observable
    public passportURLParameterRegistrationEnabled: boolean;

    private initialRegistrationFields: { [value: string]: string };

    constructor(
        registrationEnabled: boolean,
        public getIsFormApplied: () => boolean,
        registrationAsLandingPage: boolean,
        registrationApprovalEnabled: boolean,
        urlParameterRegistrationEnabled: boolean,
    ) {
        this.asLandingPage = registrationAsLandingPage;
        this.registrationAttendeeEnabled = registrationEnabled;
        this.triagedRegistrationEnabled = defaultValues.triagedRegistrationEnabled;
        this.triagedQuestionsCreated = defaultValues.triagedQuestionsCreated;
        this.registrationApprovalEnabled = registrationApprovalEnabled;
        this.urlParameterRegistrationEnabled = urlParameterRegistrationEnabled;
        this.passportRegistrationApprovalEnabled = defaultValues.registrationApprovalEnabled;
        this.passportURLParameterRegistrationEnabled = defaultValues.urlParameterRegistrationEnabled;
    }

    @mobx.action
    public toggleAttendeeRegistration = (): void => {
        this.registrationAttendeeEnabled = !this.registrationAttendeeEnabled;
        if (!this.registrationAttendeeEnabled) {
            this.asLandingPage = false;
            this.defaultSettings.calendarButtonEnabled = false;
            this.defaultSettings.autoSignInEnabled = false;
            this.defaultSettings.showOpenMeetings = false;
        }
    };

    @mobx.action.bound
    public toggleAutoSignIn(displayName: string): void {
        const settingItem = this.getSettingByFormName(displayName);
        settingItem.autoSignInEnabled = !settingItem.autoSignInEnabled;
    }

    @mobx.action.bound
    public toggleShowOpenMeetings(displayName: string): void {
        const settingItem = this.getSettingByFormName(displayName);
        settingItem.showOpenMeetings = !settingItem.showOpenMeetings;
    }

    public setInitials(): void {
        const defaultSetting = this.defaultSettings;
        this.initialRegistrationFields = {
            header: defaultSetting.header,
            instructions: defaultSetting.instructions,
            registrationSubmitMessage: defaultSetting.registrationSubmittedMessage,
            pendingDeniedMessage: defaultSetting.pendingDeniedMessage,
        };
    }

    public getShowOpenMeetingsEnabled(displayName: string): boolean {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.showOpenMeetings;
    }

    public getAutoSignInEnabled(displayName: string): boolean {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.autoSignInEnabled;
    }

    public getRegistrationEnabled = (): boolean => {
        return this.registrationAttendeeEnabled;
    };

    @mobx.action
    public onFetchGeneralRegistrationSuccess = (generalRegistrationData: IGeneralRegistrationDTO): void => {
        const {
            enabled,
            asLandingPage,
            blockedDomains,
            triagedRegistrationEnabled,
            triagedQuestionsCreated,
            settings,
            availableForms,
            registrationApprovalEnabled,
            urlParameterRegistrationEnabled,
        } = generalRegistrationData;

        this.availableForms = availableForms ?? [];
        const filteredSettings = settings.filter(s => s.formName === '' || !!this.getDisplayNameByFormName(s.formName)).map(setting => {
            const updatedSetting: IRegistrationSettings = { ...setting };
            if (setting.registrationSubmittedMessage !== undefined && setting.instructions !== undefined) {
                updatedSetting.registrationSubmittedMessage = transpileInlineStylesToClasses(setting.registrationSubmittedMessage);
                updatedSetting.instructions = transpileInlineStylesToClasses(setting.instructions);
            }
            if (setting.pendingDeniedMessage !== undefined) {
                updatedSetting.pendingDeniedMessage = transpileInlineStylesToClasses(setting.pendingDeniedMessage);
            }
            return updatedSetting;
        });

        this.settings = filteredSettings;
        this.registrationAttendeeEnabled = enabled;
        this.asLandingPage = asLandingPage || defaultValues.asLandingPage;
        this.blockedDomains = blockedDomains ? blockedDomains.map(ad => ad.domain).join('\n') : '';
        this.triagedRegistrationEnabled = triagedRegistrationEnabled || defaultValues.triagedRegistrationEnabled;
        this.triagedQuestionsCreated = triagedQuestionsCreated || defaultValues.triagedQuestionsCreated;
        this.passportRegistrationApprovalEnabled = registrationApprovalEnabled || defaultValues.registrationApprovalEnabled;
        this.passportURLParameterRegistrationEnabled = urlParameterRegistrationEnabled || defaultValues.urlParameterRegistrationEnabled;
        this.setDefaultSettingsValues();
        this.setInitials();
        const availableFormNames = availableForms.map(n => n.displayName);
        this.setSettingsFormNames([...new Set([...this.getSettingFormNames(filteredSettings), ...availableFormNames])]);
    };

    @mobx.action
    private setSettingsFormNames(formNames: string[]): void {
        this.settingsFormNames = formNames;
    }

    public get fieldsErrors(): boolean {
        // eslint-disable-next-line
        for (let index = 0; index < this.settingsFormNames.length; index++) {
            const formName = this.settingsFormNames[index];
            const errorHandler = this.registrationFieldsErrorHandler[formName];
            if (
                errorHandler?.header
                || errorHandler?.instructions
                || errorHandler?.registrationSubmittedMessage
                || errorHandler?.pendingDeniedMessage
            ) {
                return true;
            }
        }
        return false;
    }

    public checkValidity = () => {
        if (
            !this.fieldsErrors
            && !this.blockedDomainsError
        ) return true;
        else return false;
    };

    public getToTrackValues = (): { [value: string]: string } => {
        const defaultSettings = this.defaultSettings;

        return {
            'Registration toggle': isTrackingFieldEnabled(this.getRegistrationEnabled()),
            'Registration as Landing Page': isTrackingFieldEnabled(this.asLandingPage),
            'Registration Type': defaultSettings.registrationType as string,
            'Show Add to Calendar': isTrackingFieldEnabled(defaultSettings.calendarButtonEnabled),
            'One-Step Reg and Sign In': isTrackingFieldEnabled(defaultSettings.autoSignInEnabled),
            'Show Open Meetings on Reg Page': isTrackingFieldEnabled(defaultSettings.showOpenMeetings),
            ...(hasPermission(Permission.TRIAGED_REGISTRATION) ? {
                'Triaged Registration': isTrackingFieldEnabled(this.triagedRegistrationEnabled)
            } : {}),
            'Registration Header': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultSettings.header.replace(HTML_TAGS_REG_EXP, '') === defaultSettingsValues.header),
            'Registration instructions': convertBoolToDefaultOrCustomTypeForMixPanel(!defaultSettings.instructions),
            'Reg Submitted message': convertBoolToDefaultOrCustomTypeForMixPanel(
                defaultSettings.registrationSubmittedMessage.
                    replace(HTML_TAGS_REG_EXP, '') === defaultSettingsValues.registrationSubmittedMessage
            ),
            'Blocked Domains': convertBoolTypeForMixPanel(!!this.blockedDomains.length)
        };
    };

    public getGeneralRegistrationFields = (): IGeneralRegistrationDTO => {
        return {
            settings: this.settings.map(setting => ({
                ...setting,
                registrationSubmittedMessage: transpileClassesToInlineStyles(setting.registrationSubmittedMessage || ''),
                instructions: transpileClassesToInlineStyles(setting.instructions || ''),
                pendingDeniedMessage: transpileClassesToInlineStyles(setting.pendingDeniedMessage || ''),
            })),
            asLandingPage: this.asLandingPage,
            enabled: this.getRegistrationEnabled(),
            triagedRegistrationEnabled: this.triagedRegistrationEnabled,
            blockedDomains: this.blockedDomains.length
                ? this.blockedDomains.split(NEW_LINE_REG_EXP).filter(ad => !!(ad.trim())).map(ad => { return { domain: ad.trim() }; }) : [],
            registrationFieldsChanges: { ...this.getToTrackValues() },
            registrationApprovalEnabled: this.passportRegistrationApprovalEnabled,
            urlParameterRegistrationEnabled: this.passportURLParameterRegistrationEnabled,
        };
    };

    @mobx.action
    public toggleCalendarButtonLabel = (displayName: string): void => {
        const settingItem = this.getSettingByFormName(displayName);
        settingItem.calendarButtonEnabled = !settingItem.calendarButtonEnabled;
    };

    public getCalendarButton(displayName: string): boolean {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.calendarButtonEnabled;
    }

    @mobx.action
    public setRegistrationHeaderMessage = (message: string, displayName: string): void => {
        const settingItem = this.getSettingByFormName(displayName);
        this.checkRegistrationHeaderMessage(message, displayName);
        settingItem.header = message;
    };

    @mobx.action
    public checkRegistrationHeaderMessage = (message: string, displayName: string): void => {
        if (!message?.length) {
            this.registrationFieldsErrorHandler[displayName] = {
                ...this.registrationFieldsErrorHandler[displayName],
                header: REQUIRED_ERROR_MESSAGE
            };
        } else if (message.length > SHORT_PLAIN_TEXT_64_LENGTH) {
            this.registrationFieldsErrorHandler[displayName] = {
                ...this.registrationFieldsErrorHandler[displayName],
                header: MAX_LENGTH_SHORT_PLAIN_TEXT_64_NOT_REQUIRED_ERROR_MESSAGE
            };
        } else {
            this.registrationFieldsErrorHandler[displayName] = {
                ...this.registrationFieldsErrorHandler[displayName],
                header: ''
            };
        }
    };

    @mobx.action
    public setRegistrationInstructionsMessage = (message: string, displayName: string): void => {
        const settingItem = this.getSettingByFormName(displayName);
        settingItem.instructions = message;
        const error = this.checkRegistrationInstructionsMessage(message);
        this.registrationFieldsErrorHandler[displayName] = {
            ...this.registrationFieldsErrorHandler[displayName],
            instructions: error,
        };
    };

    @mobx.action
    private checkRegistrationInstructionsMessage = (message: string): string => {
        if (calcByteCodeSize(message) > LONG_RICH_TEXT_LENGTH) {
            return TEXT_TOO_LONG_ERROR_MESSAGE;
        } else {
            return '';
        }
    };

    public getRegistrationSubmitMessage(displayName: string): string {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.registrationSubmittedMessage;
    }

    @mobx.action
    private checkRegistrationSubmitMessage = (message: string): string => {
        if (!message?.length) {
            return REQUIRED_ERROR_MESSAGE;
        } else if (calcByteCodeSize(message) > LONG_RICH_TEXT_LENGTH) {
            return TEXT_TOO_LONG_ERROR_MESSAGE;
        } else {
            return '';
        }
    };

    @mobx.action
    public setRegistrationSubmitMessage = (message: string, displayName: string): void => {
        const settingItem = this.getSettingByFormName(displayName);
        settingItem.registrationSubmittedMessage = message;
        const error = this.checkRegistrationSubmitMessage(message);
        this.registrationFieldsErrorHandler[displayName] = {
            ...this.registrationFieldsErrorHandler[displayName],
            registrationSubmittedMessage: error,
        };
    };

    public getPendingDeniedMessage(displayName: string): string {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.pendingDeniedMessage;
    }

    @mobx.action
    private checkPendingDeniedMessage = (message: string): string => {
        if (!message?.length) {
            return REQUIRED_ERROR_MESSAGE;
        } else if (calcByteCodeSize(message) > LONG_RICH_TEXT_LENGTH) {
            return TEXT_TOO_LONG_ERROR_MESSAGE;
        } else {
            return '';
        }
    };

    @mobx.action
    public setPendingDeniedMessage = (message: string, displayName: string): void => {
        const settingItem = this.getSettingByFormName(displayName);
        settingItem.pendingDeniedMessage = message;
        const error = this.checkPendingDeniedMessage(message);
        this.registrationFieldsErrorHandler[displayName] = {
            ...this.registrationFieldsErrorHandler[displayName],
            pendingDeniedMessage: error,
        } as IRegistrationFieldsErrorConfig;
    };

    public getErrorMessage(displayName: string, fieldName: string): string {
        return this.registrationFieldsErrorHandler[displayName]?.[fieldName];
    }

    public getAsLandingPage(): boolean {
        return this.asLandingPage;
    }

    @mobx.action
    public setAsLandingPage = (): void => {
        this.asLandingPage = !this.asLandingPage;
    };

    public getPassportRegistrationApprovalEnabled(): boolean {
        return this.passportRegistrationApprovalEnabled;
    }

    @mobx.action
    public setPassportRegistrationApprovalEnabled = (): void => {
        this.passportRegistrationApprovalEnabled = !this.passportRegistrationApprovalEnabled;
    };

    public getPassportURLParameterRegistrationEnabled(): boolean {
        return this.passportURLParameterRegistrationEnabled;
    }

    @mobx.action
    public setPassportURLParameterRegistrationEnabled = (): void => {
        this.passportURLParameterRegistrationEnabled = !this.passportURLParameterRegistrationEnabled;
    };

    public getRegistrationType(displayName: string): RegistrationTypeValue {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.registrationType;
    }

    @mobx.action
    public setRegistrationType = (value: RegistrationTypeValue, displayName: string): void => {
        const settingItem = this.getSettingByFormName(displayName);
        if (!displayName && !this.registrationAttendeeEnabled) return;
        settingItem.registrationType = value;
    };

    public isRegistrationTypeOpen(displayName: string): boolean {
        const settingItem = this.getSettingByFormName(displayName);
        return settingItem.registrationType === RegistrationTypeValue.OPEN;
    }

    public getRegistrationAttendeeEnabled(): boolean {
        return this.registrationAttendeeEnabled;
    }

    public getBlockedDomains(): string {
        return this.blockedDomains;
    }

    public setBlockedDomains(value: string) {
        this.blockedDomains = value;
    }

    public getBlockedDomainsError(): string {
        return this.blockedDomainsError;
    }

    @mobx.action
    public setBlockedDomainsError = (intl: IntlShape) => {
        this.blockedDomainsError = null;

        if (this.blockedDomains.length) {
            const domains = this.blockedDomains.split(NEW_LINE_REG_EXP);
            if (domains.length > 50) {
                this.blockedDomainsError = intl.formatMessage({
                    id: 'oe.registration.errors.maxDomainLimit',
                    defaultMessage: 'You can enter 50 domains as max',
                    description: 'Error message text'
                });
            }
            const wrongDomains = domains.filter(d => {
                const domain = d.trim();
                if (domain && (!domain.match(DOMAIN_REG_EXP))) {
                    return true;
                }
            });
            if (wrongDomains.length) {
                this.blockedDomainsError = wrongDomains.length === 1
                    ? intl.formatMessage({
                            id: 'oe.registration.errors.wrongApprovedDomain',
                            defaultMessage: '{wrongDomain} is not a valid domain',
                            description: 'Error message text'
                        },
                        {
                            wrongDomain: wrongDomains[0]
                        }
                    )
                    : intl.formatMessage({
                            id: 'oe.registration.errors.wrongApprovedDomains',
                            defaultMessage: '{wrongDomains} \nare not valid domains',
                            description: 'Error message text'
                        },
                        {
                            wrongDomains: wrongDomains.join('\n')
                        }
                    );
            }
            const domainsWithoutEmptyStrings = this.blockedDomains.split(NEW_LINE_REG_EXP).filter(ad => ad.trim() && ad);
            const notUniqueDomains = (new Set(domainsWithoutEmptyStrings)).size !== domainsWithoutEmptyStrings.length;
            if (notUniqueDomains) {
                this.blockedDomainsError = intl.formatMessage({
                    id: 'oe.registration.errors.notUniqueDomains',
                    defaultMessage: 'You have to enter unique domains',
                    description: 'Error message text'
                });
            }
        }
    };

    @mobx.action.bound
    public toggleTriagedRegistration(): void {
        this.triagedRegistrationEnabled = !this.triagedRegistrationEnabled;
        if (!this.triagedRegistrationEnabled) {
            this.settings = [ this.defaultSettings ];
        }
    }

    public getTriagedRegistration(): boolean {
        return this.triagedRegistrationEnabled;
    }

    public getTriagedQuestionsCreated(): boolean {
        return this.triagedQuestionsCreated;
    }

    @mobx.action.bound
    public getTriagedRegistrationText(intl: IntlShape): string {
        return this.triagedRegistrationEnabled
            ? intl.formatMessage({
                id: 'oe.registration.triagedRegistration.enabled',
                defaultMessage: 'Triaged Registration is enabled',
                description: 'Text message'
            })
            : intl.formatMessage({
                id: 'oe.registration.triagedRegistration.disabled',
                defaultMessage: 'Triaged Registration is disabled',
                description: 'Text message'
            })
        ;
    }

    @mobx.action.bound
    public async onCreateTriagedQuestion(): Promise<void> {
        const isRegistrationFormSavedCorrectly = await this.generalRegistrationFormOptions.onApply();
        if (isRegistrationFormSavedCorrectly) {
            this.generalRegistrationFormOptions.closeConfigureRegistrationForm();
            this.showFormRegistrationFormPopup();
        }
    }

    private getSettingFormNames(settings: IRegistrationSettings[]): string[] {
        return settings.map(s => this.getDisplayNameByFormName(s.formName));
    }

    public getSettingByFormName(displayName: string): IRegistrationSettings {
        return this.settings.find(s => s.formName === this.getFormNameByDisplayName(displayName));
    }

    public get defaultSettings(): IRegistrationSettings {
        return this.settings.find(s => s.formName === '');
    }

    @mobx.action
    private setDefaultSettingsValues = (): void => {
        this.settings.forEach(s => {
            s.header = s.header || defaultSettingsValues.header;
            s.instructions = s.instructions || defaultSettingsValues.instructions;
            s.registrationSubmittedMessage = s.registrationSubmittedMessage || defaultSettingsValues.registrationSubmittedMessage;
            s.pendingDeniedMessage = s.pendingDeniedMessage;
        });
    };

    @mobx.action
    public toggleTriageAnswerWithinSettings(triageAnswer: string): void {
        if (triageAnswer === '') return;
        if (!isFieldExistInArray(this.settings, 'formName', this.getFormNameByDisplayName(triageAnswer))) {
            this.settings = [...this.settings, {
                ...this.defaultSettings,
                formName: this.getFormNameByDisplayName(triageAnswer),
            }];
            this.setRegistrationHeaderMessage(this.defaultSettings.header, triageAnswer);
            this.setRegistrationInstructionsMessage(this.defaultSettings.instructions, triageAnswer);
            this.setRegistrationSubmitMessage(this.defaultSettings.registrationSubmittedMessage, triageAnswer);
            this.setPendingDeniedMessage(this.defaultSettings.pendingDeniedMessage, triageAnswer);
        } else {
            this.settings = this.settings.filter(s => s.formName !== this.getFormNameByDisplayName(triageAnswer));
            delete this.registrationFieldsErrorHandler[triageAnswer];
        }
    }

    public getFormNameByDisplayName = (displayName: string): string => {
        return this.availableForms.find(f => f.displayName === displayName)?.formName;
    };

    public getDisplayNameByFormName = (formName: string): string => {
        return this.availableForms.find(f => f.formName === formName)?.displayName;
    };
}
